/** @jsxImportSource @emotion/react */
import { ReactNode, forwardRef, Ref } from 'react';
import { DialogProps } from 'reakit';
import { useTheme } from '@emotion/react';
import { SystemStyleObject } from '@styled-system/css';
import { pxToRem } from '@mos-cat/ds-theme';

import invariant from 'tiny-invariant';
import { StylesOptions } from '../hooks/useStyles';
import { useRequiredPropWarning } from '../hooks/useRequiredPropWarning';
import { scrollStyles } from '../utils/useScrollStyles';

import { Box } from '../Box/Box';
import { Heading } from '../Heading/Heading';
import { Stack } from '../Stack/Stack';
import { ModalBackdrop } from './ModalBackdrop';
import { ModalContainer } from './ModalContainer';
import { ModalCloseButton } from './ModalCloseButton';

export { useDialogState as useModalState } from 'reakit';
export { ModalDisclosure } from './ModalDisclosure';

export type ModalProps = Omit<DialogProps, 'color'> & {
  heading: ReactNode;
  headingContent?: ReactNode;
  modalFooter?: ReactNode;
  modalSize?: 'small' | 'default';

  // свойства ниже запрещены к использованию
  preventBodyScroll?: never | undefined;
  hideOnEsc?: never | undefined;
  hideOnClickOutside?: never | undefined;
} & StylesOptions;

const modalWidth = {
  small: ['100%', 'modalSmallMaxWidth'],
  default: ['100%', 'modalMaxWidth'],
};

/**
 * @public
 */
export const Modal = forwardRef(
  (
    /* eslint-disable @typescript-eslint/no-unused-vars -- preventBodyScroll, hideOnEsc, hideOnClickOutside добавлены сюда, чтобы они не ломали поведение даже если проброшены */
    {
      heading,
      headingContent,
      modalFooter,
      preventBodyScroll,
      hideOnEsc,
      hideOnClickOutside,
      modalSize = 'default',
      ...props
    }: ModalProps,
    /* eslint-enable @typescript-eslint/no-unused-vars -- продолжаем проверять */
    ref: Ref<HTMLDivElement>
  ): JSX.Element => {
    useRequiredPropWarning(Boolean(heading), 'Modal', 'heading');
    invariant(
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- нужна проверка на конкретные значения
      modalSize === 'small' || modalSize === 'default',
      'Свойство modalSize может иметь только значение small или default'
    );

    let theme = useTheme();

    return (
      <ModalBackdrop {...props}>
        <ModalContainer
          {...props}
          maxHeight={['modalMaxHeight', 'modalMaxHeightBig']}
          maxWidth={modalWidth[modalSize]}
          ref={ref}
          aria-label={typeof heading === 'string' ? heading : undefined}
        >
          {/* Обертка содержимого модалки */}
          <Box
            styles={
              {
                '@media (min-width: 48em)': scrollStyles(theme),
                label: 'ModalContent',
              } as SystemStyleObject
            }
            display="flex"
            flexDirection="column"
            flexGrow={1}
            position="relative"
            overflow="auto"
            maxHeight={1}
            minHeight={1}
            width={1}
          >
            {/* Блок заголовка */}
            <Box
              borderBottomWidth="modalHeaderBorder"
              borderColor="modalDividerColor"
              pt={['modalHeaderPaddingTopSmall', 'modalHeaderPaddingTop']}
              pr={['modalHeaderPaddingRightSmall', 'modalHeaderPaddingRight']}
              pb={['modalHeaderPaddingBottomSmall', 'modalHeaderPaddingBottom']}
              pl={['modalHeaderPaddingLeftSmall', 'modalHeaderPaddingLeft']}
              styles={{ label: 'ModalHeader' }}
            >
              <Heading
                as="h2"
                level={[3, 2]}
                tabIndex={0}
                styles={{
                  marginBottom: 'modalHeadingMarginBottom',
                  outline: 0,
                  pr: [
                    'modalHeadingPaddingRightSmall, modalHeadingPaddingRight',
                  ],

                  '&:last-child': {
                    marginBottom: 0,
                  },
                }}
              >
                {heading}
              </Heading>
              {headingContent}
            </Box>

            {/* Блок тела */}
            <Stack
              space={['sp-32', 'sp-40']}
              flexGrow={1}
              pt={['modalBodyPaddingTopSmall', 'modalBodyPaddingTop']}
              pr={['modalBodyPaddingRightSmall', 'modalBodyPaddingRight']}
              pb={['modalBodyPaddingBottomSmall', 'modalBodyPaddingBottom']}
              pl={['modalBodyPaddingLeftSmall', 'modalBodyPaddingLeft']}
              styles={{ label: 'ModalBody' }}
            >
              {props.children}
            </Stack>
          </Box>

          {/* Блок закрепленного футера */}
          {modalFooter && (
            <Box
              py={['sp-16', pxToRem(20)]}
              px={['sp-16', 'sp-40']}
              bg="white"
              borderTop="1"
              borderColor="modalDividerColor"
              position="relative"
              zIndex={1010}
              styles={{ label: 'ModalFooter' }}
            >
              {modalFooter}
            </Box>
          )}
          <ModalCloseButton hide={props.hide} />
        </ModalContainer>
      </ModalBackdrop>
    );
  }
);
